import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../../supabaseClient.jsx";
import { getBabaPhotoCandidates, handleBabaPhotoError } from "../../../../utils/babaPhoto.js";
import { Search, Star, MapPin, ArrowRight } from "lucide-react";
import { Button, Card, Input } from "../../../../design-system/index.js";
import { cn } from "../../../../design-system/index.js";

export default function HighlightsSection() {
  const [babas, setBabas] = useState([]);
  const [filteredBabas, setFilteredBabas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const formatArea = (area) => {
    if (!area) return "";
    if (Array.isArray(area)) return area.join(", ");
    try {
      if (typeof area === "string" && area.trim().startsWith("[")) {
        const parsed = JSON.parse(area);
        return Array.isArray(parsed) ? parsed.join(", ") : area;
      }
    } catch (e) {}
    return area;
  };

  useEffect(() => {
    async function carregarBabas() {
      try {
        const { data, error } = await supabase
          .from("view_babas_publicas")
          .select("*")
          .order("avaliacao_media", { ascending: false, nullsLast: true })
          .order("total_avaliacoes", { ascending: false })
          .order("created_at", { ascending: false });

        if (error) { console.error("Erro ao carregar babás:", error); return; }
        if (data) {
          const babasComUrl = data.map((baba) => {
            const fotoCandidates = getBabaPhotoCandidates(baba.foto_url);
            return { ...baba, fotoCandidates, displayFotoUrl: fotoCandidates[0] };
          });
          setBabas(babasComUrl);
          setFilteredBabas(babasComUrl);
        }
      } catch (err) {
        console.error("Erro inesperado ao buscar babás:", err);
      }
    }
    carregarBabas();
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    setFilteredBabas(
      babas.filter((baba) => {
        const nomeCompleto = `${baba.nome} ${baba.sobrenome}`.toLowerCase();
        const area = formatArea(baba.area_atuacao || "").toLowerCase();
        return nomeCompleto.includes(term) || area.includes(term);
      }),
    );
  }, [searchTerm, babas]);

  return (
    <section id="babas-profissionais" className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className={cn(
            "font-heading text-3xl sm:text-4xl font-bold",
            "text-[var(--color-warm-900)]"
          )}>
            Profissionais em Destaque
          </h2>
          <p className={cn(
            "mt-3 text-[var(--color-warm-500)] max-w-md mx-auto",
            "text-sm sm:text-base"
          )}>
            Babás verificadas, avaliadas e prontas para cuidar do seu tesourinho.
          </p>
        </div>

        {/* Busca */}
        <div className="mx-auto max-w-md mb-8">
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-warm-400)]" />
            <Input
              type="text"
              placeholder="Buscar babá por nome ou área..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-full py-2.5 pl-9 pr-4 text-sm"
            />
          </div>
        </div>

        {/* Grid */}
        {filteredBabas.length === 0 ? (
          <p className="text-center text-sm text-[var(--color-warm-400)] py-8">
            Nenhuma babá encontrada com esse termo.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredBabas.map((baba) => {
              const nomeCompleto = `${baba.nome} ${baba.sobrenome}`;
              const nota = baba.avaliacao_media || 0;
              const totalReviews = baba.total_avaliacoes || 0;

              return (
                <Card
                  key={baba.id}
                  className={cn(
                    "group cursor-pointer rounded-2xl overflow-hidden",
                    "hover:shadow-md hover:-translate-y-1 transition-all duration-200"
                  )}
                  onClick={() => navigate("/login")}
                >
                  {/* Foto */}
                  <div className="aspect-square overflow-hidden bg-[var(--color-warm-100)]">
                    <img
                      src={baba.displayFotoUrl}
                      alt={nomeCompleto}
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => handleBabaPhotoError(e, baba.fotoCandidates)}
                    />
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h3 className="font-semibold text-sm text-[var(--color-warm-900)] truncate">{nomeCompleto}</h3>

                    {baba.cidade && (
                      <p className="flex items-center gap-1 text-xs text-[var(--color-warm-400)] mt-0.5">
                        <MapPin size={10} /> {baba.cidade}
                      </p>
                    )}

                    {baba.bio && (
                      <p className="text-xs text-[var(--color-warm-500)] mt-1.5 line-clamp-2 leading-relaxed">
                        {baba.bio}
                      </p>
                    )}

                    {nota > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        <Star size={12} className="fill-amber-400 text-amber-400" />
                        <span className="text-xs font-semibold text-[var(--color-warm-800)]">
                          {nota.toFixed(1)}
                        </span>
                        {totalReviews > 0 && (
                          <span className="text-xs text-[var(--color-warm-400)]">({totalReviews})</span>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Ver todas */}
        <div className="text-center mt-10">
          <Button
            variant="outline"
            size="lg"
            className={cn(
              "rounded-full border-2 border-[var(--color-rose-400)]",
              "px-6 py-2.5 font-bold text-[var(--color-rose-400)]",
              "hover:bg-[var(--color-rose-400)] hover:text-white"
            )}
            onClick={() => navigate("/babas")}
          >
            Ver todas as babás <ArrowRight size={15} />
          </Button>
        </div>
      </div>
    </section>
  );
}